import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { css, html } from '../../lib/lit.js';

// The assets channel is a single setting, not a list: one Discord channel that
// D3 Assets notifications are posted to, unless an event names a freelancer's
// own channel. Unset, they go to #d3-staff. The picker lists every channel the
// bot can post in, private ones included.
class AssetsChannelView extends BaseComponent {
    static properties = {
        _channel: { type: Object, state: true },
        _channels: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _notice: { type: String, state: true },
        _channelID: { type: String, state: true },
        _saving: { type: Boolean, state: true },
    };

    static styles = css`
        :host { display: block; }
    `;

    constructor() {
        super();
        this._channel = null;
        this._channels = [];
        this._loading = true;
        this._error = null;
        this._notice = null;
        this._channelID = '';
        this._saving = false;
    }

    async connectedCallback() {
        super.connectedCallback();
        await this._load();
    }

    async _load() {
        this._loading = true;
        this._error = null;
        try {
            const [channel, channels] = await Promise.all([
                api.get('/api/assets-channel'),
                api.get('/api/discord-channels'),
            ]);
            this._channels = channels ?? [];
            this._setChannel(channel);
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    _setChannel(channel) {
        this._channel = channel;
        const id = channel?.DiscordChannelID ?? '';
        // A saved channel missing from the picker can't be re-saved as is.
        this._channelID = this._channels.some((c) => c.id === id) ? id : '';
    }

    async _save(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        this._notice = null;
        try {
            this._setChannel(
                await api.put('/api/assets-channel', {
                    discord_channel_id: this._channelID,
                }),
            );
            this._notice = 'Saved.';
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _clear() {
        this._error = null;
        this._notice = null;
        try {
            await api.delete('/api/assets-channel');
            this._setChannel(null);
            this._notice = 'Cleared. Assets notifications will post to #d3-staff.';
        } catch (err) {
            this._error = err.message;
        }
    }

    async _test() {
        this._error = null;
        this._notice = null;
        try {
            await api.post('/api/assets-channel/test');
            this._notice = 'Test notification sent.';
        } catch (err) {
            this._error = err.message;
        }
    }

    // _groups puts the channels under their categories, in the order the API
    // sorted them (uncategorized first).
    _groups() {
        const groups = new Map();
        for (const channel of this._channels) {
            const key = channel.category || '';
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(channel);
        }
        return [...groups.entries()];
    }

    _option(channel) {
        return html`
            <option value="${channel.id}" ?selected="${channel.id === this._channelID}">
                ${channel.private ? '🔒 ' : '#'}${channel.name}
            </option>
        `;
    }

    render() {
        if (this._loading) {
            return html`<div class="text-center py-4"><span class="spinner-border"></span></div>`;
        }

        // A saved channel the bot can no longer post in is still shown, so the
        // page doesn't pretend it is unset.
        const savedMissing =
            this._channel && !this._channels.some((c) => c.id === this._channel.DiscordChannelID);

        return html`
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0">Assets Channel</h5>
                ${
                    this._channel
                        ? html`
                    <div class="d-flex gap-2">
                        <button class="btn btn-outline-info btn-sm" title="Send test notification" @click="${this._test}">
                            <i class="bi bi-send"></i> Test
                        </button>
                        <button class="btn btn-outline-danger btn-sm" title="Clear the assets channel" @click="${this._clear}">
                            <i class="bi bi-x-circle"></i> Clear
                        </button>
                    </div>
                `
                        : ''
                }
            </div>

            <p class="text-muted" style="font-size: 0.875rem;">
                D3 Assets commission, SOW and MSA notifications are posted to this channel. A
                notification for a freelancer with their own channel goes there instead, and comes
                here with a note if D3PO can't post there. With no channel set, or if posting here
                fails, they go to #d3-staff.
            </p>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}
            ${this._notice ? html`<div class="alert alert-success">${this._notice}</div>` : ''}
            ${
                savedMissing
                    ? html`<div class="alert alert-warning">
                          D3PO can't post in the saved channel (<code>${this._channel.DiscordChannelID}</code>)
                          any more, so notifications are going to #d3-staff. Pick another channel, or give
                          D3PO access to it.
                      </div>`
                    : ''
            }

            <form @submit="${(e) => this._save(e)}" style="max-width: 500px;">
                <div class="mb-3">
                    <label class="form-label">Discord Channel</label>
                    <select class="form-select" required
                            @change="${(e) => {
                                this._channelID = e.target.value;
                            }}">
                        <option value="" disabled ?selected="${!this._channelID || savedMissing}">Select a channel…</option>
                        ${this._groups().map(([category, channels]) =>
                            category
                                ? html`<optgroup label="${category}">${channels.map((c) => this._option(c))}</optgroup>`
                                : channels.map((c) => this._option(c)),
                        )}
                    </select>
                    <div class="form-text">
                        Every channel D3PO can post in. 🔒 marks a private one. To use a private channel
                        that isn't listed, add D3PO to it in Discord, then reload this page.
                    </div>
                </div>

                <button type="submit" class="btn btn-primary" ?disabled="${this._saving || !this._channelID}">
                    ${this._saving ? html`<span class="spinner-border spinner-border-sm"></span>` : 'Save'}
                </button>
            </form>
        `;
    }
}

customElements.define('assets-channel-view', AssetsChannelView);
